import { gql, useMutation, useQuery } from '@apollo/client'
import flatten from 'lodash/flatten'
import { useMemo } from 'react'

import {
  ArtistSubscriptionPlan,
  ArtistSubscriptionPlanQuery,
  ArtistSubscriptionPlanQueryVariables,
  ArtistUpdatePreSaleMutation,
  ArtistUpdatePreSaleMutationVariables,
  ConsolePreSaleEditFragment,
  ConsolePreSaleEditFragmentDoc,
  ConsolePreSaleEditQuery,
  ConsolePreSaleEditQueryVariables,
  MembershipInterval,
  MembershipSubscriptionPlan,
  PaymentMethod,
  PreSaleType,
} from '@/clients/utoniq-core/schema'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'

import { Action, State } from './template'
import { LotteryEditValues } from './template/LotteryEditForm'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router, generatePath, routerPages } =
    useRouter<{ preSaleId: string; eventId: string }>()
  const { showMessage, showError } = useMessage()
  const { t } = useTranslation('consolePreSaleEdit')
  const { data } = useQuery<
    ConsolePreSaleEditQuery,
    ConsolePreSaleEditQueryVariables
  >(QUERY, {
    variables: {
      id: router.query.preSaleId,
    },
  })
  const preSaleType = data?.artistPreSale?.type

  const { data: artistSubscriptionPlanData } = useQuery<
    ArtistSubscriptionPlanQuery,
    ArtistSubscriptionPlanQueryVariables
  >(ArtistSubscriptionPlan)

  const plans = useMemo(() => {
    if (artistSubscriptionPlanData?.artist?.subscription?.products?.length) {
      return flatten(
        artistSubscriptionPlanData?.artist?.subscription?.products.map(
          product => {
            const monthPlan = product.plans.find(
              plan => plan.interval === MembershipInterval.Month
            )
            if (monthPlan) {
              return [{ ...monthPlan, title: product.title }]
            }
            return product.plans.map(plan => ({
              ...plan,
              title: product.title,
            }))
          }
        )
      )
    }
    if (artistSubscriptionPlanData?.artist?.subscription?.plans?.length) {
      return artistSubscriptionPlanData.artist.subscription.plans
    }
    return []
  }, [
    artistSubscriptionPlanData?.artist?.subscription?.plans,
    artistSubscriptionPlanData?.artist?.subscription?.products,
  ])

  const transPlanIdToPurchesablePlanIds = (
    preSale: ConsolePreSaleEditFragment,
    plans: Array<
      Pick<MembershipSubscriptionPlan, 'planId' | 'title' | 'interval'> & {
        title?: string
        amount?: number
      }
    >
  ) => {
    // isPremiumがtrueで、purchesablePlanIdsが空の場合は、全てのプランを取得
    if (preSale.isPremium && preSale.purchesablePlanIds?.length === 0) {
      return plans.map(plan => plan?.planId ?? '')
    }
    // planIdが設定されている場合は、そのプランの金額以下のプランを取得
    if (preSale.planId) {
      const targetPlan = plans?.find(plan => plan?.planId === preSale.planId)
      const currentPlans = plans?.filter(plan =>
        plan?.amount && targetPlan?.amount
          ? plan.amount <= targetPlan.amount
          : false
      )
      return currentPlans?.map(plan => plan?.planId ?? '') ?? []
    } else {
      return (
        preSale.purchesablePlanIds?.filter((id): id is string => id !== null) ??
        []
      )
    }
  }

  const [updatePreSale, { loading: submitting }] = useMutation<
    ArtistUpdatePreSaleMutation,
    ArtistUpdatePreSaleMutationVariables
  >(UPDATE_PRESALE)

  return {
    state: {
      preSale: data?.artistPreSale
        ? {
            ...data.artistPreSale,
            purchesablePlanIds: transPlanIdToPurchesablePlanIds(
              data.artistPreSale,
              plans
            ),
          }
        : null,
      artistId: user?.uid ?? '',
      plans,
      submitting,
    },
    actions: {
      async onSubmit(values) {
        const {
          description,
          endedAt,
          isPublished,
          note,
          startedAt,
          title,
          purchesablePlanIds,
          isPremium,
        } = values

        await updatePreSale({
          variables: {
            id: router.query.preSaleId,
            inputs: {
              description,
              endedAt,
              isPublished,
              note,
              startedAt,
              title,
              purchesablePlanIds,
              itemIds: data?.artistPreSale?.itemIds ?? [],
              isPremium,
              planId: null,
              paymentMethods:
                values.paymentMethods === 'both'
                  ? [PaymentMethod.CreditCard, PaymentMethod.Konbini]
                  : [values.paymentMethods as PaymentMethod],
              lottery:
                preSaleType === PreSaleType.Lottery
                  ? {
                      applicationEndedAt: (values as LotteryEditValues)
                        .applicationEndedAt,
                      applicationStartedAt: (values as LotteryEditValues)
                        .applicationStartedAt,
                      maxChoice: (values as LotteryEditValues).maxChoice,
                      purchasableCount: (values as LotteryEditValues)
                        .purchasableCount,
                      purchasableEndedAt: values.purchasableEndedAt,
                      purchasableStartedAt: values.purchasableStartedAt,
                      resultNoticedAt: (values as LotteryEditValues)
                        .resultNoticedAt,
                    }
                  : undefined,
              firstComeFirstServed:
                preSaleType === 'first_come_first_served'
                  ? {
                      purchasableCount: values.purchasableCount,
                      purchasableEndedAt: values.purchasableEndedAt,
                      purchasableStartedAt: values.purchasableStartedAt,
                    }
                  : undefined,
            },
          },
        })
          .then(() => {
            showMessage(t('successMessage', '先行販売チケットを更新しました'))
            router.push(
              generatePath(routerPages.ConsoleShokufesEventDetail, {
                eventId: router.query.eventId,
              })
            )
          })
          .catch(showError)
      },
    },
  }
})

const QUERY = gql`
  query ConsolePreSaleEdit($id: ID!) {
    artistPreSale(id: $id) {
      ...ConsolePreSaleEdit
    }
  }
  ${ConsolePreSaleEditFragmentDoc}
`

const UPDATE_PRESALE = gql`
  mutation ArtistUpdatePreSale($id: ID!, $inputs: ArtistUpdatePreSaleInput!) {
    artistUpdatePreSale(id: $id, inputs: $inputs) {
      id
      isPublished
      lottery {
        resultNoticedAt
      }
    }
  }
`
